import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, Typography, Box, IconButton, Tooltip
} from '@mui/material';
import { Close, CloudDownload, Refresh, ContentCopy } from '@mui/icons-material';
import { useData } from '../../hooks/useData';
import { LoadingSpinner } from '../Common';
import { DynamicIcon } from '../icons';
import { useNotification } from '../../context/NotificationContext';
import { apiFetch } from '../../api';
import { useTimezoneFormatter } from '../../hooks/useTimezoneFormatter';

const RunHistoryDialog = ({ open, onClose, datasetId, token }) => {
  const { formatDate: formatDateTz } = useTimezoneFormatter();
  const { addNotification } = useNotification();
  const { data: runs, isLoading, refresh } = useData(
    open ? `/datasets/${datasetId}/runs` : null,
    token
  );

  // --- Auto-Refresh ---
  // Poll every 5 seconds while the dialog is open
  React.useEffect(() => {
    if (!open) return;
    const interval = setInterval(() => {
      refresh(true);
    }, 5000);
    return () => clearInterval(interval);
  }, [open, refresh]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return formatDateTz(dateStr);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const handleCancel = async (runId) => {
    try {
      // console.log(`[FE] Cancelling specific run: ${runId}`);
      await apiFetch(`/datasets/runs/${runId}/cancel`, token, { method: 'POST' });
      addNotification("Cancellation requested", "info");
      // console.log(`[FE] Run ${runId} cancelled successfully`);
      refresh(true);
    } catch (err) {
      console.error("[FE] Failed to cancel run:", err);
      addNotification(`Cancellation failed: ${err.message}`, "error");
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      {/* Updated Title with Dataset ID */}
      <DialogTitle sx={{ display: 'flex', flexDirection: 'column', gap: 1, borderBottom: '1px solid #eee' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <Box display="flex" alignItems="center" gap={1}>
            <DynamicIcon name="History" />
            <Typography variant="h6">Run History</Typography>
          </Box>
          <IconButton onClick={refresh} size="small"><Refresh /></IconButton>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary', fontSize: '0.8rem', fontFamily: 'monospace' }}>
          <span>Dataset ID: {datasetId}</span>
          <Tooltip title="Copy ID">
            <ContentCopy sx={{ fontSize: 14, cursor: 'pointer' }} onClick={() => copyToClipboard(datasetId)} />
          </Tooltip>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {isLoading && <Box p={4}><LoadingSpinner /></Box>}

        {!isLoading && runs && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell>Status</TableCell>
                  <TableCell>Run Date</TableCell>
                  <TableCell>Duration</TableCell>
                  <TableCell>Rows</TableCell>
                  <TableCell>Size</TableCell>
                  <TableCell>S3 Filename / Target</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {runs.map((run) => {
                  const fileName = run.storage_target ? run.storage_target.split('/').pop() : '-';
                  return (
                    <TableRow key={run.id} hover>
                      <TableCell>
                        <Chip
                          label={run.status}
                          size="small"
                          color={run.status === 'completed' ? 'success' : run.status === 'failed' ? 'error' : 'warning'}
                          variant="outlined"
                          sx={{ fontSize: '0.7rem', height: 20 }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>{formatDate(run.started_at)}</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>
                        {run.finished_at
                          ? `${Math.round(dayjs(run.finished_at).diff(dayjs(run.started_at)) / 1000)}s`
                          : '...'}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>{run.row_count?.toLocaleString() || '-'}</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>{run.file_size_mb ? `${run.file_size_mb} MB` : '-'}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem', color: 'text.secondary', maxWidth: 200 }} title={run.storage_target}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{fileName}</span>
                          {run.storage_target && (
                            <Tooltip title="Copy Full Path">
                              <ContentCopy sx={{ fontSize: 12, cursor: 'pointer' }} onClick={() => copyToClipboard(run.storage_target)} />
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        {(run.status === 'pending' || run.status === 'running') && (
                          <Button
                            size="small"
                            color="error"
                            variant="text"
                            onClick={() => handleCancel(run.id)}
                            sx={{ fontSize: '0.65rem', py: 0 }}
                          >
                            Cancel
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {runs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      No runs found. Click "Build" to generate data.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog >
  );
};

export default RunHistoryDialog;